import { auth } from '../config/firebase';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export interface StockAssignment {
  orderId: string;
  platform: string;
  eventName: string;
  qty: number;
  priceTotal: number;
  at: string;
}

export interface StockItem {
  _id: string;
  kind: 'event' | 'season';
  eventName: string;
  eventDate?: string | null;
  team: string;
  quantity: number;
  category: string;
  split: string;
  pricePerTicket: number; // per game per seat for season items (derived from seasonTotalPrice)
  seasonTotalPrice?: number; // season items: full-season price of ONE seat
  seasonGames?: number; // season items: home games the season covers (divisor)
  boughtFrom: string;
  notes: string;
  assignments: StockAssignment[];
  assignedTotal: number;
  remaining?: number; // event stock
  // Season stock: the team's home fixtures (plus any assigned-but-unlisted events), each with usage.
  usage?: { eventName: string; date: string | null; used: number; remaining: number }[];
  // Season stock: fixtures marked as not covered by the season ticket (friendlies etc.).
  excludedList?: { eventName: string; date: string | null }[];
  createdAt?: string;
}

// Season items: mark a fixture as not covered by the season ticket / restore it.
export async function excludeStockEvent(id: string, eventName: string) {
  await axios.post(
    `${API_URL}/stock/${id}/exclude-event`,
    { eventName },
    { headers: await authHeaders() },
  );
}
export async function includeStockEvent(id: string, eventName: string) {
  await axios.post(
    `${API_URL}/stock/${id}/include-event`,
    { eventName },
    { headers: await authHeaders() },
  );
}

export interface StockEventOption {
  name: string;
  date: string | null;
}

// Upcoming fixtures (from the market-data livefootballevents) for the add-stock autocomplete.
export async function listStockEvents(): Promise<StockEventOption[]> {
  const { data } = await axios.get(`${API_URL}/stock/events`, { headers: await authHeaders() });
  return data;
}

export interface StockCandidate {
  orderId: string;
  platform: string;
  eventName: string;
  eventDate: string | null;
  quantity: number;
  buyerName: string;
  remainingForThisGame: number;
}

export async function listStock(): Promise<StockItem[]> {
  const { data } = await axios.get(`${API_URL}/stock`, { headers: await authHeaders() });
  return data;
}

export async function createStock(item: Partial<StockItem>): Promise<StockItem> {
  const { data } = await axios.post(`${API_URL}/stock`, item, { headers: await authHeaders() });
  return data;
}

export async function updateStock(id: string, patch: Partial<StockItem>): Promise<StockItem> {
  const { data } = await axios.patch(`${API_URL}/stock/${id}`, patch, {
    headers: await authHeaders(),
  });
  return data;
}

export async function deleteStock(id: string): Promise<void> {
  await axios.delete(`${API_URL}/stock/${id}`, { headers: await authHeaders() });
}

export async function stockCandidates(
  id: string,
): Promise<{ stock: StockItem; candidates: StockCandidate[] }> {
  const { data } = await axios.get(`${API_URL}/stock/${id}/candidates`, {
    headers: await authHeaders(),
  });
  return data;
}

export async function assignStock(
  id: string,
  orderId: string,
  platform: string,
  qty: number,
): Promise<{ ok: boolean; stock: StockItem }> {
  const { data } = await axios.post(
    `${API_URL}/stock/${id}/assign`,
    { orderId, platform, qty },
    { headers: await authHeaders() },
  );
  return data;
}

export async function unassignStock(
  id: string,
  orderId: string,
  platform: string,
): Promise<{ ok: boolean; stock: StockItem }> {
  const { data } = await axios.post(
    `${API_URL}/stock/${id}/unassign`,
    { orderId, platform },
    { headers: await authHeaders() },
  );
  return data;
}
